import React, { useState } from "react";

/**
 * Magic-link checking page.
 *
 * Reads the one-time sign-in token from the URL fragment (#token=...) so it
 * never reaches the server in a Referer header or appears in proxy logs.
 *
 * IMPORTANT — the token is NOT auto-redeemed on page load. Redemption happens
 * only in response to an explicit user click on the "Sign in" button. This
 * is defence in depth against automated fetchers (URL security scanners,
 * link-preview crawlers, prefetchers, headless-browser sandboxes, RSS readers,
 * etc.) that would otherwise consume the single-use token before the human
 * ever clicks. The token is single-use by design; anything that consumes it
 * before the human does causes a "This link is invalid, has been used, or has
 * expired" message on the real click. A required human interaction breaks
 * that class of failure without weakening the underlying single-use property.
 *
 * The token is captured during render via a lazy useState initializer so the
 * component is idempotent under remount (React 18 StrictMode double-mount in
 * dev, HMR, error-boundary retries, or parent-triggered re-mounts). The URL
 * fragment is wiped inside handleSignIn() — i.e. at click time, not on mount
 * — so a remount always re-derives the same state from the same URL. Wiping
 * on click is safe because the token is single-use with a 20-minute TTL and
 * becomes dead the moment the user clicks.
 *
 * On success we render the confirmation IN PLACE — we do NOT navigate to
 * /dashboard, because /dashboard is guarded by the LEGACY auth context
 * (separate from the handover session cookie) and would otherwise bounce the
 * user to the password-login form.
 */
function readTokenFromLocationHash() {
  if (typeof window === "undefined") return "";
  const hash = window.location.hash || "";
  const m = hash.match(/(?:^|[#&])token=([A-Za-z0-9_\-]+)/);
  return m ? m[1] : "";
}

export default function MagicLinkChecking() {
  // Capture the token during render — BEFORE any effect wipes the fragment.
  // Using a lazy initializer guarantees this runs once per component instance,
  // so remounts (StrictMode, HMR, error-boundary) safely re-read the same
  // still-intact URL fragment.
  const [token] = useState(readTokenFromLocationHash);

  // ready    → token present in fragment, waiting for user click
  // checking → POST /redeem in flight
  // success  → redeem succeeded
  // error    → no token OR redeem failed
  const [state, setState] = useState(() => (token ? "ready" : "error"));
  const [entitlements, setEntitlements] = useState([]);

  async function handleSignIn() {
    if (!token || state === "checking") return;
    // Wipe the fragment from the URL bar and history the moment the user
    // commits to redeeming the link, so the raw token no longer lingers.
    try {
      window.history.replaceState(null, "", window.location.pathname);
    } catch (_) {
      /* history API unavailable — token becomes dead on the server anyway */
    }
    setState("checking");
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/handover/redeem`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        }
      );
      if (!r.ok) {
        setState("error");
        return;
      }
      const body = await r.json().catch(() => ({}));
      const ents = Array.isArray(body.entitlements) ? body.entitlements : [];
      setEntitlements(ents);

      // If MagicLinkRequest stashed a safe ?next=<path> destination in
      // localStorage, honour it now so members return to the exact page
      // they were trying to open (e.g. /care-rooms, an invited room URL).
      // Only accept same-origin absolute paths.
      let next = null;
      try {
        const raw = window.localStorage.getItem("jcc_handover_next");
        if (typeof raw === "string") {
          const v = raw.trim();
          if (v.startsWith("/") && !v.startsWith("//")) next = v;
        }
        window.localStorage.removeItem("jcc_handover_next");
      } catch {
        /* storage unavailable — fall back to the hub buttons below */
      }

      if (next) {
        // Full navigation guarantees the new handover cookie is included
        // on the first request to the destination. Use replace() so the
        // one-time /auth/handover/checking URL is removed from browser
        // history and Back-navigation never lands on a token-less
        // checking page (which would render "Sign-in link unavailable").
        window.location.replace(next);
        return;
      }

      setState("success");
    } catch {
      setState("error");
    }
  }

  return (
    <main
      className="mx-auto max-w-md px-6 py-24 text-center"
      data-testid="magic-link-checking-page"
    >
      {state === "ready" && (
        <div data-testid="magic-link-ready">
          <h1 className="text-2xl font-semibold">Confirm your sign-in</h1>
          <p className="mt-3 text-sm text-zinc-600">
            For security, we ask you to confirm this sign-in with one click.
            Your one-time link stays unused until you press the button below.
          </p>
          <button
            type="button"
            onClick={handleSignIn}
            data-testid="magic-link-confirm-signin-button"
            className="mt-8 inline-block rounded-md bg-zinc-900 px-5 py-2.5
                       text-sm font-medium text-white hover:bg-zinc-800"
          >
            Sign in to Just Care Community
          </button>
          <p className="mt-6 text-xs text-zinc-500">
            If you did not request this link, you can safely close this tab.
          </p>
        </div>
      )}

      {state === "checking" && (
        <>
          <h1 className="text-lg font-semibold">Checking your secure link…</h1>
          <p className="mt-2 text-sm text-zinc-600">One moment please.</p>
        </>
      )}

      {state === "success" && (
        <div data-testid="magic-link-success">
          <h1 className="text-2xl font-semibold">You&rsquo;re signed in</h1>
          <p className="mt-3 text-sm text-zinc-600">
            Your Just Care Community membership is active for this browser.
          </p>
          {entitlements.length > 0 && (
            <div
              className="mt-6 flex flex-wrap justify-center gap-2"
              data-testid="magic-link-entitlements"
            >
              {entitlements.map((e) => (
                <span
                  key={e}
                  className="rounded-full bg-zinc-900 px-3 py-1 text-xs
                             font-medium text-white tracking-wide"
                >
                  {e}
                </span>
              ))}
            </div>
          )}
          {entitlements.includes("carer_free") && (
            <a
              href="/workspace/carer-starter-hub"
              onClick={(e) => { e.preventDefault(); window.location.replace("/workspace/carer-starter-hub"); }}
              data-testid="magic-link-continue-carer-starter"
              className="mt-8 inline-block rounded-md bg-teal-600 px-5 py-2.5
                         text-sm font-medium text-white hover:bg-teal-700"
            >
              Continue to your Free Carer Hub
            </a>
          )}
          {entitlements.includes("family_free") && (
            <a
              href="/workspace/family-starter-hub"
              onClick={(e) => { e.preventDefault(); window.location.replace("/workspace/family-starter-hub"); }}
              data-testid="magic-link-continue-family-starter"
              className="mt-4 inline-block rounded-md bg-teal-600 px-5 py-2.5
                         text-sm font-medium text-white hover:bg-teal-700"
            >
              Continue to your Free Family Hub
            </a>
          )}
          {entitlements.includes("pro_carer") && (
            <a
              href="/workspace/pro-carer-circle/start-here"
              onClick={(e) => { e.preventDefault(); window.location.replace("/workspace/pro-carer-circle/start-here"); }}
              data-testid="magic-link-continue-pro-carer-circle"
              className="mt-4 inline-block rounded-md bg-purple-700 px-5 py-2.5
                         text-sm font-medium text-white hover:bg-purple-800"
            >
              Continue to your Pro Carer Office
            </a>
          )}
          {entitlements.includes("family_circle") && (
            <a
              href="/workspace/family-circle-hub"
              onClick={(e) => { e.preventDefault(); window.location.replace("/workspace/family-circle-hub"); }}
              data-testid="magic-link-continue-family-circle"
              className="mt-4 inline-block rounded-md bg-purple-700 px-5 py-2.5
                         text-sm font-medium text-white hover:bg-purple-800"
            >
              Continue to your Family Care Office
            </a>
          )}
          <p className="mt-8 text-xs text-zinc-500">
            You can close this tab, or open the member area when ready.
          </p>
        </div>
      )}

      {state === "error" && (
        <>
          <h1 className="text-lg font-semibold">Sign-in link unavailable</h1>
          <p
            className="mt-2 text-sm text-zinc-600"
            data-testid="magic-link-checking-error"
          >
            This link is invalid, has been used, or has expired. Please request
            a fresh one.
          </p>
          <a
            href="/auth/handover/request"
            className="mt-6 inline-block rounded-md bg-zinc-900 px-4 py-2
                       text-sm font-medium text-white"
          >
            Request a new link
          </a>
        </>
      )}
    </main>
  );
}
